import type {
  ProfitabilityReportRow,
  ProfitabilityDimension,
} from '@erp/shared-types';

/**
 * Maps supplier internal codes to their parent group name.
 * Suppliers in the same group will be rolled up under a single expandable parent row.
 */
export const SUPPLIER_GROUP_MAP: Record<string, string> = {
  STA01: 'Stanpro/Aimlite/Beluce',
  STA02: 'Stanpro/Aimlite/Beluce',
  AIM01: 'Stanpro/Aimlite/Beluce',
  EAT01: 'Eaton',
  COO02: 'Eaton',
  COO03: 'Eaton',
  COO04: 'Eaton',
  COO05: 'Eaton',
};

function sumField(
  rows: ProfitabilityReportRow[],
  field: keyof ProfitabilityReportRow,
): number {
  return rows.reduce((sum, row) => sum + ((row[field] as number) || 0), 0);
}

function buildGroupRow(
  groupName: string,
  children: ProfitabilityReportRow[],
): ProfitabilityReportRow {
  const sumCogs = sumField(children, 'cogs');
  const sumNonConditional = sumField(children, 'nonConditional');
  const sumConditional = sumField(children, 'conditional');
  const sumRedistribution = sumField(children, 'redistribution');
  const sumTotalRebates = sumField(children, 'totalRebates');

  // COGS-weighted average for landed margin
  const landedMarginPercent =
    sumCogs !== 0
      ? children.reduce(
          (acc, child) => acc + child.cogs * child.landedMarginPercent,
          0,
        ) / sumCogs
      : 0;

  const nonConditionalPercent =
    sumCogs !== 0 ? (sumNonConditional / sumCogs) * 100 : 0;
  const conditionalPercent =
    sumCogs !== 0 ? (sumConditional / sumCogs) * 100 : 0;
  const redistributionPercent =
    sumCogs !== 0 ? (sumRedistribution / sumCogs) * 100 : 0;
  const totalRebatesPercent =
    sumCogs !== 0 ? (sumTotalRebates / sumCogs) * 100 : 0;
  const totalProfitPercent = landedMarginPercent + totalRebatesPercent;

  return {
    dimensionLabel: groupName,
    dimensionType: 'supplier' as ProfitabilityDimension,
    internalCode: children
      .map((c) => c.internalCode)
      .filter(Boolean)
      .join(', '),
    cogs: sumCogs,
    landedMarginPercent,
    nonConditional: sumNonConditional,
    nonConditionalPercent,
    conditional: sumConditional,
    conditionalPercent,
    redistribution: sumRedistribution,
    redistributionPercent,
    totalRebates: sumTotalRebates,
    totalRebatesPercent,
    totalProfitPercent,
    children,
  };
}

/**
 * Groups supplier rows that belong to the same supplier group under a synthetic parent row.
 * Only applies when the first dimension is "supplier".
 * Grouped rows become 4-level (group → supplier → dim1 → dim2) instead of the default 3-level.
 */
export function groupSupplierRows(
  rows: ProfitabilityReportRow[],
  dimensions: ProfitabilityDimension[],
): ProfitabilityReportRow[] {
  if (dimensions[0] !== 'supplier') {
    return rows;
  }

  const ungrouped: ProfitabilityReportRow[] = [];
  const groupMap = new Map<string, ProfitabilityReportRow[]>();

  for (const row of rows) {
    const groupName = row.internalCode
      ? SUPPLIER_GROUP_MAP[row.internalCode]
      : undefined;

    if (groupName) {
      const existing = groupMap.get(groupName);
      if (existing) {
        existing.push(row);
      } else {
        groupMap.set(groupName, [row]);
      }
    } else {
      ungrouped.push(row);
    }
  }

  const groupRows: ProfitabilityReportRow[] = [];
  for (const [groupName, children] of groupMap) {
    groupRows.push(buildGroupRow(groupName, children));
  }

  // Combine and sort by COGS descending
  return [...ungrouped, ...groupRows].sort((a, b) => b.cogs - a.cogs);
}
